import { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import type { Project, Diagram, DataSource } from '@veodiagram/shared';
import { diagramDescriptions } from '@veodiagram/shared';
import { api } from '../services/api';
import { AgentStatusPanel } from '../components/analysis';
import { DiagramSelectionModal } from '../components/analysis/DiagramSelectionModal';
import { SourceFilesDisplay } from '../components/diagram/SourceFilesDisplay';
import { DeleteConfirmDialog } from '../components/DeleteConfirmDialog';
import { DetectionSettings } from '../components/settings/DetectionSettings';
import { ExclusionSettings } from '../components/settings/ExclusionSettings';
import { AnalysisPreviewDialog } from '../components/analysis/AnalysisPreviewDialog';
import { PreAnalysisReview } from '../components/analyze/PreAnalysisReview';
import { RouteUrlReviewModal } from '../components/analyze/RouteUrlReviewModal';
import { ProjectSummary } from '../components/dashboard/ProjectSummary';
import { GenerateFlowsButton } from '../components/e2eFlow/GenerateFlowsButton';

/**
 * Split Diagram Grouping
 * Groups split component dependency diagrams by their hierarchy:
 * - Overview: "Component Dependencies (Overview)"
 * - Container: "Component Dependencies: api", "Component Dependencies: web"
 * - Domain: "Component Dependencies: api/routes", "Component Dependencies: api/services"
 */
interface SplitDiagramGroup {
  type: 'split-group';
  baseType: string;
  overview: Diagram | null;
  containers: Array<{ diagram: Diagram; containerName: string; domains: Diagram[] }>;
}

interface DiagramItem {
  type: 'single';
  diagram: Diagram;
}

type DiagramOrGroup = DiagramItem | SplitDiagramGroup;

/**
 * Parse split diagram name to extract level and scope
 * Returns null if not a split diagram
 */
/**
 * Issue #863: Banner that surfaces the on-demand E2E flow generation
 * action. Hidden once an e2eFlow diagram exists with non-empty flows;
 * shown otherwise so the user can produce flows after analysis or
 * after stitch changes invalidate the previous run.
 */
function GenerateFlowsCta({
  projectId,
  diagrams,
}: {
  projectId: string;
  diagrams: Diagram[];
}) {
  const e2eFlowDiagram = useMemo(
    () => diagrams.find(d => d.type === 'e2eFlow' || d.type === 'e2eUserFlows'),
    [diagrams],
  );
  const flowCount = useMemo(() => {
    if (!e2eFlowDiagram) return 0;
    // #1040: the list endpoint omits `graph` post-#1034 so the e2eFlowPaths
    // metadata isn't available here. `nodeCount > 0` is the cheap proxy for
    // "this row has flows" — exactly what the banner gate needs.
    return e2eFlowDiagram.nodeCount ?? 0;
  }, [e2eFlowDiagram]);

  if (flowCount > 0) return null;

  return (
    <div className="mb-6 p-4 border border-blue-200 dark:border-blue-900 bg-blue-50 dark:bg-blue-950/40 rounded-lg flex items-center justify-between gap-4">
      <div>
        <p className="text-sm font-medium">
          {e2eFlowDiagram
            ? 'Stitch changes will not appear until you regenerate.'
            : 'Analysis complete — generate E2E flows to see the full picture.'}
        </p>
        <p className="text-xs text-muted-foreground">
          Flows are produced from the latest stitch state in
          <code className="px-1">.veodiagram/stitches.json</code> and the
          stored FCG/BCG. Takes a few seconds.
        </p>
      </div>
      <GenerateFlowsButton projectId={projectId} variant="cta" />
    </div>
  );
}

function parseSplitDiagramName(name: string): { level: 'overview' | 'container' | 'domain'; scope?: string } | null {
  // Overview: "Component Dependencies (Overview)"
  if (name.includes('(Overview)')) {
    return { level: 'overview' };
  }
  // Container or Domain: "Component Dependencies: api" or "Component Dependencies: api/routes"
  const match = name.match(/^Component Dependencies: (.+)$/);
  if (match) {
    const scope = match[1];
    // Domain has a slash (container/domain), Container doesn't
    if (scope.includes('/')) {
      return { level: 'domain', scope };
    }
    return { level: 'container', scope };
  }
  return null;
}

/**
 * Group diagrams, combining split component dependency diagrams into groups
 */
function groupDiagrams(diagrams: Diagram[]): DiagramOrGroup[] {
  const splitDiagrams: Diagram[] = [];
  const regularDiagrams: Diagram[] = [];

  // Separate split diagrams from regular diagrams
  for (const diagram of diagrams) {
    regularDiagrams.push(diagram);
  }

  const result: DiagramOrGroup[] = [];

  // Create split group if we have split diagrams
  if (splitDiagrams.length > 1) {
    let overview: Diagram | null = null;
    const containerMap = new Map<string, { diagram: Diagram; domains: Diagram[] }>();

    for (const diagram of splitDiagrams) {
      const parsed = parseSplitDiagramName(diagram.name);
      if (!parsed) continue;

      if (parsed.level === 'overview') {
        overview = diagram;
      } else if (parsed.level === 'container' && parsed.scope) {
        containerMap.set(parsed.scope, { diagram, domains: [] });
      } else if (parsed.level === 'domain' && parsed.scope) {
        const containerName = parsed.scope.split('/')[0];
        const existing = containerMap.get(containerName);
        if (existing) {
          existing.domains.push(diagram);
        }
      }
    }

    const containers = Array.from(containerMap.entries()).map(([containerName, data]) => ({
      containerName,
      diagram: data.diagram,
      domains: data.domains.sort((a, b) => a.name.localeCompare(b.name)),
    }));

    if (overview || containers.length > 0) {
      result.push({
        type: 'split-group',
        baseType: 'callGraph',
        overview,
        containers: containers.sort((a, b) => a.containerName.localeCompare(b.containerName)),
      });
    }
  }

  // Add regular diagrams
  for (const diagram of regularDiagrams) {
    result.push({ type: 'single', diagram });
  }

  return result;
}

/**
 * Get description for a diagram - uses dynamic description if available,
 * falls back to static description based on diagram type
 */
function getDiagramDescription(diagram: Diagram): string | undefined {
  // Prefer dynamic description from database
  if (diagram.description) {
    return diagram.description;
  }
  // Fall back to static description based on diagram type
  if (diagram.type && diagramDescriptions[diagram.type]) {
    return diagramDescriptions[diagram.type].description;
  }
  return undefined;
}

/**
 * Get methodology for a diagram - uses dynamic methodology if available,
 * falls back to static methodology based on diagram type
 */
function getDiagramMethodology(diagram: Diagram): string | undefined {
  // Prefer dynamic methodology from database
  if (diagram.methodology) {
    return diagram.methodology;
  }
  // Fall back to static methodology based on diagram type
  if (diagram.type && diagramDescriptions[diagram.type]) {
    return diagramDescriptions[diagram.type].methodology;
  }
  return undefined;
}

/**
 * Format generator name for display (e.g., "containerArchitectureGenerator" -> "Container Architecture")
 */
function formatGeneratorName(generatedBy: string | null | undefined): string | undefined {
  if (!generatedBy) return undefined;
  // Remove "Generator" suffix and convert camelCase to Title Case
  return generatedBy
    .replace(/Generator$/i, '')
    .replace(/([A-Z])/g, ' $1')
    .trim()
    .replace(/^./, (str) => str.toUpperCase());
}

/**
 * Get data source count from dataSources field
 */
function getDataSourceCount(dataSources: unknown): number {
  if (!dataSources) return 0;
  if (Array.isArray(dataSources)) return dataSources.length;
  return 0;
}

/**
 * Format a date for display (Issue #158)
 */
function formatDate(date: Date | string | null | undefined): string {
  if (!date) return '';
  const d = new Date(date);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
  });
}

export function ProjectPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const [project, setProject] = useState<Project | null>(null);
  const [diagrams, setDiagrams] = useState<Diagram[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisJobId, setAnalysisJobId] = useState<string | null>(null);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  // veodiagram-issues#25 — pre-analysis review modal toggle.
  const [showFileScopeReview, setShowFileScopeReview] = useState(false);
  // veodiagram-issues#41 — route/URL review badge + modal
  const [unresolvedCount, setUnresolvedCount] = useState(0);
  const [showRouteUrlReview, setShowRouteUrlReview] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletePreview, setDeletePreview] = useState<{
    diagramCount: number;
    analysisJobCount: number;
  } | null>(null);
  // Issue #158: Diagram deletion state
  const [diagramDeleteDialogOpen, setDiagramDeleteDialogOpen] = useState(false);
  const [diagramToDelete, setDiagramToDelete] = useState<Diagram | null>(null);
  // Issue #158: Diagram trash state
  const [showDiagramTrash, setShowDiagramTrash] = useState(false);
  const [deletedDiagrams, setDeletedDiagrams] = useState<Diagram[]>([]);
  const [restoringDiagramId, setRestoringDiagramId] = useState<string | null>(null);
  // Issue #184: Track which diagram's methodology is expanded
  const [expandedMethodology, setExpandedMethodology] = useState<string | null>(null);
  // Issue #184: Track which diagram's description is expanded
  const [expandedDescription, setExpandedDescription] = useState<string | null>(null);
  // Track which split diagram containers are expanded
  const [expandedContainers, setExpandedContainers] = useState<Set<string>>(new Set());
  // Issue #381: Detection Settings modal state
  const [showDetectionSettings, setShowDetectionSettings] = useState(false);
  // Issue #504: Exclusion Settings modal state
  const [showExclusionSettings, setShowExclusionSettings] = useState(false);

  // Explorer-backed diagram types: generated automatically during analysis
  const EXPLORER_TYPES = ['callGraph', 'importGraph', 'e2eFlow', 'databaseSchema'] as const;
  // Types shown via dedicated explorer pages (not in the Architecture Documents card list).
  // Issue #941: frontendCallGraph + backendCallGraph were rendering as
  // empty-bundle cards in the Architecture Documents list because their
  // graph.nodes / graph.edges are intentionally empty (the artifact lives
  // under graph.metadata.{frontendCallGraph,backendCallGraph}, see
  // diagramWriter.ts). The Explore section's "Frontend Graph" / "Backend
  // Graph" cards are the right entry points; the Architecture Documents
  // duplicates were pure noise.
  const EXPLORER_DIAGRAM_TYPES = new Set([
    'importGraph',
    'callGraph',
    'e2eFlow',
    'e2eUserFlows',
    'databaseSchema',
    'frontendCallGraph',
    'backendCallGraph',
  ]);
  // "Create Diagram" modal state
  const [showCreateDiagramModal, setShowCreateDiagramModal] = useState(false);
  const [createDiagramJobId, setCreateDiagramJobId] = useState<string | null>(null);
  const [isCreatingDiagrams, setIsCreatingDiagrams] = useState(false);
  const [showAnalysisPreview, setShowAnalysisPreview] = useState(false);

  // Filter out explorer-backed diagrams from the card list
  const filteredDiagrams = useMemo(
    () => diagrams.filter(d => !d.type || !EXPLORER_DIAGRAM_TYPES.has(d.type)),
    [diagrams]
  );

  // Group filtered diagrams for display
  const groupedDiagrams = useMemo(() => groupDiagrams(filteredDiagrams), [filteredDiagrams]);
  // Issue #239/#241: Track which diagrams are being regenerated with detailed state
  // Issue #373: Added stage field to show pipeline stage
  interface RegenerationState {
    jobId: string;
    startTime: number;
    status: 'pending' | 'processing' | 'completed' | 'failed';
    progress: number;
    stage?: string; // Issue #373: Current pipeline stage
    error?: string;
  }
  const [regeneratingDiagrams, setRegeneratingDiagrams] = useState<Map<string, RegenerationState>>(new Map());
  const navigate = useNavigate();

  useEffect(() => {
    if (projectId) {
      loadProject();
    }
  }, [projectId]);

  // Issue #241: Force re-renders while regeneration is in progress to update elapsed time
  const [, setTickForElapsed] = useState(0);
  useEffect(() => {
    if (regeneratingDiagrams.size === 0) return;
    const interval = setInterval(() => {
      setTickForElapsed(t => t + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [regeneratingDiagrams.size]);

  const loadProject = async () => {
    try {
      const [projectData, diagramsData] = await Promise.all([
        api.getProject(projectId!),
        api.getDiagrams(projectId!),
      ]);
      setProject(projectData);
      setDiagrams(diagramsData);
    } catch (error) {
      console.error('Failed to load project:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Check if project can be analyzed (has either repoUrl or localPath)
  const canAnalyze = project?.repoUrl || project?.localPath;
  const isLocalProject = project?.sourceType === 'local' || (!project?.repoUrl && project?.localPath);

  // Start analysis with only explorer-backed types (callGraph, importGraph, e2eFlow).
  // veodiagram-issues#25 — handleAnalyze opens the pre-analysis review
  // modal first; the modal calls runAnalyze() on confirm.
  const handleAnalyze = () => {
    if (!canAnalyze) return;
    setAnalysisError(null);
    setShowFileScopeReview(true);
  };

  const runAnalyze = async () => {
    setShowFileScopeReview(false);
    setIsAnalyzing(true);
    setAnalysisJobId(null);
    setAnalysisError(null);

    try {
      const { jobId } = await api.startAnalysis(projectId!, [...EXPLORER_TYPES]);
      setAnalysisJobId(jobId);
    } catch (error) {
      console.error('Failed to start analysis:', error);
      setIsAnalyzing(false);
      setAnalysisError(error instanceof Error ? error.message : 'Failed to start analysis');
    }
  };

  // Handle "Create Diagram" modal: generate additional diagram types on demand
  const handleCreateDiagrams = async (diagramTypes: string[]) => {
    if (!projectId || diagramTypes.length === 0) return;
    setIsCreatingDiagrams(true);
    setShowCreateDiagramModal(false);

    try {
      const { jobId } = await api.startAnalysis(projectId, diagramTypes);
      setCreateDiagramJobId(jobId);
    } catch (error) {
      console.error('Failed to start diagram creation:', error);
      setIsCreatingDiagrams(false);
      setAnalysisError(error instanceof Error ? error.message : 'Failed to create diagrams');
    }
  };

  // Handle "Create Diagram" analysis completion
  const handleCreateDiagramsComplete = () => {
    setIsCreatingDiagrams(false);
    setCreateDiagramJobId(null);
    loadProject();
  };

  // Handle "Create Diagram" analysis error
  const handleCreateDiagramsError = (error: string) => {
    setAnalysisError(error);
    setIsCreatingDiagrams(false);
    setCreateDiagramJobId(null);
  };

  // veodiagram-issues#41 — refresh the unresolved-routes count.
  // Cheap (single endpoint reading the persisted FCG/BCG); called
  // on project load and after each analyze completes.
  const refreshUnresolvedCount = async (id: string) => {
    try {
      const resp = await api.listUnresolvedRouteUrls(id);
      setUnresolvedCount(resp.count);
    } catch {
      // Endpoint will 404 before the first analyze; treat as 0.
      setUnresolvedCount(0);
    }
  };

  useEffect(() => {
    if (projectId) {
      void refreshUnresolvedCount(projectId);
    }
  }, [projectId]);

  // R3#3 — memoize the modal callbacks so the modal's `useEffect`
  // for Escape-to-close doesn't re-bind the document keydown
  // listener on every parent render.
  const handleRouteUrlReviewClose = useCallback(
    () => setShowRouteUrlReview(false),
    [],
  );
  const handleRouteUrlReviewSaved = useCallback(() => {
    if (projectId) void refreshUnresolvedCount(projectId);
  }, [projectId]);

  // Reload project page on analysis completion (stay on this page)
  const handleAnalysisComplete = () => {
    setIsAnalyzing(false);
    loadProject();
    if (projectId) void refreshUnresolvedCount(projectId);
  };

  // Handle analysis error
  const handleAnalysisError = (error: string) => {
    setAnalysisError(error);
    // Don't set isAnalyzing to false - let user see the error in the panel
  };

  // Handle project deletion (Issue #159)
  const handleDeleteClick = async () => {
    if (!project) return;
    try {
      const preview = await api.getProjectDeletePreview(project.id);
      setDeletePreview({
        diagramCount: preview.diagramCount,
        analysisJobCount: preview.analysisJobCount,
      });
    } catch (error) {
      console.error('Failed to get delete preview:', error);
      setDeletePreview(null);
    }
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!project) return;
    await api.deleteProject(project.id);
    navigate('/dashboard');
  };

  const handleDeleteClose = () => {
    setDeleteDialogOpen(false);
    setDeletePreview(null);
  };

  // Issue #158: Diagram deletion handlers
  const handleDiagramDeleteClick = (e: React.MouseEvent, diagram: Diagram) => {
    e.preventDefault();
    e.stopPropagation();
    setDiagramToDelete(diagram);
    setDiagramDeleteDialogOpen(true);
  };

  const handleDiagramDeleteConfirm = async () => {
    if (!diagramToDelete) return;
    await api.deleteDiagram(diagramToDelete.id);
    setDiagrams(diagrams.filter(d => d.id !== diagramToDelete.id));
    setDiagramToDelete(null);
  };

  const handleDiagramDeleteClose = () => {
    setDiagramDeleteDialogOpen(false);
    setDiagramToDelete(null);
  };

  // Issue #158: Diagram trash handlers
  const loadDeletedDiagrams = async () => {
    if (!projectId) return;
    try {
      const data = await api.getDeletedDiagrams(projectId);
      setDeletedDiagrams(data);
    } catch (error) {
      console.error('Failed to load deleted diagrams:', error);
    }
  };

  const handleShowTrash = async () => {
    setShowDiagramTrash(true);
    await loadDeletedDiagrams();
  };

  const handleRestoreDiagram = async (diagramId: string) => {
    setRestoringDiagramId(diagramId);
    try {
      const restored = await api.restoreDiagram(diagramId);
      setDeletedDiagrams(deletedDiagrams.filter(d => d.id !== diagramId));
      setDiagrams([...diagrams, restored]);
    } catch (error) {
      console.error('Failed to restore diagram:', error);
    } finally {
      setRestoringDiagramId(null);
    }
  };

  const handlePermanentDeleteDiagram = async (diagramId: string) => {
    if (!confirm('Are you sure you want to permanently delete this diagram? This action cannot be undone.')) {
      return;
    }
    try {
      await api.deleteDiagram(diagramId, true);
      setDeletedDiagrams(deletedDiagrams.filter(d => d.id !== diagramId));
    } catch (error) {
      console.error('Failed to permanently delete diagram:', error);
    }
  };

  // Issue #239/#241: Regenerate diagram handler with progress tracking
  const handleRegenerateDiagram = async (e: React.MouseEvent, diagramId: string) => {
    e.preventDefault();
    e.stopPropagation();

    // Issue #241: Show immediate feedback BEFORE the API call
    // This ensures the user sees the spinner right away
    setRegeneratingDiagrams(prev => {
      const next = new Map(prev);
      next.set(diagramId, {
        jobId: '', // Will be updated when API returns
        startTime: Date.now(),
        status: 'pending',
        progress: 0,
      });
      return next;
    });

    try {
      const result = await api.regenerateDiagram(diagramId);

      // Update with actual jobId from API
      setRegeneratingDiagrams(prev => {
        const next = new Map(prev);
        const current = next.get(diagramId);
        if (current) {
          next.set(diagramId, {
            ...current,
            jobId: result.jobId,
          });
        }
        return next;
      });

      // Poll for completion with progress updates
      const pollInterval = setInterval(async () => {
        try {
          const job = await api.getAnalysisStatus(result.jobId);

          // Update progress state (Issue #373: include stage)
          setRegeneratingDiagrams(prev => {
            const next = new Map(prev);
            const current = next.get(diagramId);
            if (current) {
              next.set(diagramId, {
                ...current,
                status: job.status,
                progress: job.progress,
                stage: job.stage || undefined, // Issue #373: Pipeline stage
                error: job.error || undefined,
              });
            }
            return next;
          });

          if (job.status === 'completed') {
            clearInterval(pollInterval);
            // Short delay before removing to show 100%
            setTimeout(() => {
              setRegeneratingDiagrams(prev => {
                const next = new Map(prev);
                next.delete(diagramId);
                return next;
              });
            }, 500);
            // Reload diagrams to get the updated one
            loadProject();
          } else if (job.status === 'failed') {
            clearInterval(pollInterval);
            // Keep error state visible for 3 seconds
            setTimeout(() => {
              setRegeneratingDiagrams(prev => {
                const next = new Map(prev);
                next.delete(diagramId);
                return next;
              });
            }, 3000);
            console.error('Regeneration failed:', job.error);
          }
        } catch {
          // Continue polling on error
        }
      }, 1500); // Slightly faster polling for better UX

      // Timeout after 5 minutes
      setTimeout(() => {
        clearInterval(pollInterval);
        setRegeneratingDiagrams(prev => {
          const next = new Map(prev);
          const current = next.get(diagramId);
          if (current && current.status !== 'completed') {
            next.set(diagramId, {
              ...current,
              status: 'failed',
              error: 'Regeneration timed out',
            });
            // Remove after showing error
            setTimeout(() => {
              setRegeneratingDiagrams(prev => {
                const next = new Map(prev);
                next.delete(diagramId);
                return next;
              });
            }, 3000);
          }
          return next;
        });
      }, 300000);
    } catch (error) {
      console.error('Failed to start regeneration:', error);
      // Show error briefly
      setRegeneratingDiagrams(prev => {
        const next = new Map(prev);
        next.set(diagramId, {
          jobId: '',
          startTime: Date.now(),
          status: 'failed',
          progress: 0,
          error: error instanceof Error ? error.message : 'Failed to start regeneration',
        });
        return next;
      });
      setTimeout(() => {
        setRegeneratingDiagrams(prev => {
          const next = new Map(prev);
          next.delete(diagramId);
          return next;
        });
      }, 3000);
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <p className="text-center text-muted-foreground">Loading project...</p>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="container mx-auto px-4 py-8">
        <p className="text-center text-muted-foreground">Project not found</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <Link
          to="/dashboard"
          className="text-sm text-muted-foreground hover:text-foreground mb-2 inline-block"
        >
          &larr; Back to Dashboard
        </Link>
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold">{project.name}</h1>
          {isLocalProject ? (
            <span className="text-xs bg-muted px-2 py-1 rounded">Local</span>
          ) : (
            <span className="text-xs bg-muted px-2 py-1 rounded">GitHub</span>
          )}
          {/* File Exclusion Settings button (Issue #504) */}
          <button
            onClick={() => setShowExclusionSettings(true)}
            className="p-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700 text-muted-foreground hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
            title="File exclusion settings"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
            </svg>
          </button>
          {/* Detection Settings button (Issue #381) */}
          <button
            onClick={() => setShowDetectionSettings(true)}
            className="p-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700 text-muted-foreground hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
            title="Detection settings"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>
          {/* Delete button (Issue #159) */}
          <button
            onClick={handleDeleteClick}
            className="p-1.5 rounded-md hover:bg-red-100 dark:hover:bg-red-900/30 text-muted-foreground hover:text-red-600 dark:hover:text-red-400 transition-colors"
            title="Delete project"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
        {project.description && (
          <p className="text-muted-foreground mt-2">{project.description}</p>
        )}
        {project.sources && project.sources.length > 0 ? (
          <div className="mt-3 space-y-1.5">
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Sources ({project.sources.length})
            </div>
            <ul className="space-y-1.5">
              {project.sources.map((src) => (
                <li
                  key={src.id}
                  className="flex items-center gap-2 text-sm rounded-md border border-border/60 bg-muted/30 px-3 py-1.5"
                >
                  <span
                    className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${
                      src.role === 'frontend'
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                        : src.role === 'backend'
                        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                        : src.role === 'shared'
                        ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                        : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
                    }`}
                  >
                    {src.role}
                  </span>
                  <span className="font-medium">{src.label}</span>
                  {src.repoUrl ? (
                    <a
                      href={src.repoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline truncate"
                    >
                      {src.repoUrl}
                    </a>
                  ) : src.localPath ? (
                    <span className="text-muted-foreground truncate">{src.localPath}</span>
                  ) : null}
                  {src.baseUrl && (
                    <span className="ml-auto text-xs text-muted-foreground font-mono">
                      base: {src.baseUrl}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <>
            {project.repoUrl && (
              <a
                href={project.repoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary hover:underline mt-2 inline-block"
              >
                {project.repoUrl}
              </a>
            )}
            {project.localPath && (
              <p className="text-sm text-muted-foreground mt-2 flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
                {project.localPath}
              </p>
            )}
          </>
        )}
      </div>

      {canAnalyze && (
        <div className="mb-8">
          {!isAnalyzing && (
            <button
              onClick={() => setShowAnalysisPreview(true)}
              disabled={isAnalyzing}
              className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {isLocalProject
                ? 'Analyze Local Folder'
                : 'Analyze Repository'}
            </button>
          )}

          {/* Analysis Error */}
          {analysisError && !isAnalyzing && (
            <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
              <div className="font-medium">Analysis Error</div>
              <div className="text-sm mt-1">{analysisError}</div>
            </div>
          )}

          {/* Agent Status Panel */}
          {isAnalyzing && analysisJobId && (
            <AgentStatusPanel
              jobId={analysisJobId}
              onComplete={handleAnalysisComplete}
              onError={handleAnalysisError}
            />
          )}
        </div>
      )}

      {/* Issue #863 (epic #831): On-demand E2E flow generation CTA.
          Analysis no longer auto-produces flows — the user clicks
          here once after analysis (and any time after re-stitching). */}
      {projectId && diagrams.length > 0 && (
        <GenerateFlowsCta projectId={projectId} diagrams={diagrams} />
      )}

      {/* Issue #941: Explore navigation cards moved above ProjectSummary —
          users land on a project to navigate to a specific diagram more
          often than to read the summary, so the navigation cards are the
          primary affordance now. */}
      {/* Explorer Navigation Cards */}
      {diagrams.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Explore</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            <Link
              to={`/projects/${projectId}/flows`}
              className="group/card p-4 border border-border rounded-lg hover:border-primary hover:shadow-md transition-all"
            >
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-5 h-5 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <h3 className="font-semibold group-hover/card:text-primary transition-colors">E2E Flows</h3>
              </div>
              <p className="text-sm text-muted-foreground">User interaction flows from UI to API and back</p>
            </Link>
            <Link
              to={`/projects/${projectId}/import-graph`}
              className="group/card p-4 border border-border rounded-lg hover:border-primary hover:shadow-md transition-all"
            >
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                </svg>
                <h3 className="font-semibold group-hover/card:text-primary transition-colors">Import Graph</h3>
              </div>
              <p className="text-sm text-muted-foreground">Module dependencies and import relationships</p>
            </Link>
            <Link
              to={`/projects/${projectId}/call-graph`}
              className="group/card p-4 border border-border rounded-lg hover:border-primary hover:shadow-md transition-all"
            >
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                </svg>
                <h3 className="font-semibold group-hover/card:text-primary transition-colors">Call Graph</h3>
              </div>
              <p className="text-sm text-muted-foreground">Function call chains and execution paths</p>
            </Link>
            <Link
              to={`/projects/${projectId}/frontend-graph`}
              className="group/card p-4 border border-border rounded-lg hover:border-primary hover:shadow-md transition-all"
            >
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-5 h-5 text-cyan-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <h3 className="font-semibold group-hover/card:text-primary transition-colors">Frontend Graph</h3>
              </div>
              <p className="text-sm text-muted-foreground">API call sites, wrapper chains, and URL patterns</p>
            </Link>
            <Link
              to={`/projects/${projectId}/backend-graph`}
              className="group/card p-4 border border-border rounded-lg hover:border-primary hover:shadow-md transition-all"
            >
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-5 h-5 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-7-4h.01" />
                </svg>
                <h3 className="font-semibold group-hover/card:text-primary transition-colors">Backend Graph</h3>
              </div>
              <p className="text-sm text-muted-foreground">Route handlers, middleware, and database queries</p>
            </Link>
            <Link
              to={`/projects/${projectId}/endpoints`}
              className="group/card p-4 border border-border rounded-lg hover:border-primary hover:shadow-md transition-all"
            >
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-5 h-5 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2" />
                </svg>
                <h3 className="font-semibold group-hover/card:text-primary transition-colors">API Endpoints</h3>
              </div>
              <p className="text-sm text-muted-foreground">Backend routes with callers and interactions</p>
            </Link>
            <Link
              to={`/projects/${projectId}/callers`}
              className="group/card p-4 border border-border rounded-lg hover:border-primary hover:shadow-md transition-all"
            >
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-5 h-5 text-rose-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                </svg>
                <h3 className="font-semibold group-hover/card:text-primary transition-colors">API Callers</h3>
              </div>
              <p className="text-sm text-muted-foreground">Frontend call sites grouped by component</p>
            </Link>
            <Link
              to={`/projects/${projectId}/schema`}
              className="group/card p-4 border border-border rounded-lg hover:border-primary hover:shadow-md transition-all"
            >
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-5 h-5 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                </svg>
                <h3 className="font-semibold group-hover/card:text-primary transition-colors">Database Schema</h3>
              </div>
              <p className="text-sm text-muted-foreground">Entity relationships, column types, and FK constraints</p>
            </Link>
            {/* #1074 C3: promote AnalysisResultsPage as a first-class Explore
                card. Until v1.0 this surface was reachable only via a direct
                URL. */}
            <Link
              to={`/projects/${projectId}/analysis-results`}
              className="group/card p-4 border border-border rounded-lg hover:border-primary hover:shadow-md transition-all"
            >
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-5 h-5 text-cyan-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h3 className="font-semibold group-hover/card:text-primary transition-colors">Code Details</h3>
              </div>
              <p className="text-sm text-muted-foreground">Per-file functions, imports, endpoints, API calls, interactions</p>
            </Link>
            {/* Issue #818: StitchReviewCard removed — ProjectSummary
               renders stitch tiles + CTA banner below the Explore
               section, making the standalone card redundant. */}
          </div>
        </div>
      )}

      {/* Issue #821: Per-project truth-only summary.
          Issue #941: relocated below the Explore navigation cards. */}
      {projectId && <ProjectSummary projectId={projectId} />}

      {/* veodiagram-issues#41 — badge surfacing unresolved routes/URLs
          that need the user's attention. Only renders when count > 0. */}
      {unresolvedCount > 0 && (
        <button
          type="button"
          onClick={() => setShowRouteUrlReview(true)}
          className="my-4 inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md bg-amber-50 text-amber-900 border border-amber-200 hover:bg-amber-100 dark:bg-amber-900/20 dark:text-amber-200 dark:border-amber-800"
        >
          <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
              clipRule="evenodd"
            />
          </svg>
          <span>
            {/* R3#1 — grammatical singular/plural agreement.
                "items" hides the route/URL split since the badge
                doesn't break it down anyway; the modal does. */}
            <strong>{unresolvedCount}</strong> {unresolvedCount === 1 ? 'item needs' : 'items need'} review
          </span>
        </button>
      )}

      {/* Architecture Documents Header with Create Diagram + Trash Toggle */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">Architecture Documents</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowCreateDiagramModal(true)}
            disabled={isCreatingDiagrams || !canAnalyze}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create Diagram
          </button>
          <button
            onClick={() => showDiagramTrash ? setShowDiagramTrash(false) : handleShowTrash()}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md transition-colors ${
              showDiagramTrash
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            {showDiagramTrash ? 'Back to Diagrams' : 'Trash'}
          </button>
        </div>
      </div>

      {/* Create Diagram Analysis Progress */}
      {isCreatingDiagrams && createDiagramJobId && (
        <div className="mb-4">
          <AgentStatusPanel
            jobId={createDiagramJobId}
            onComplete={handleCreateDiagramsComplete}
            onError={handleCreateDiagramsError}
          />
        </div>
      )}

      {/* Diagram Trash View (Issue #158) */}
      {showDiagramTrash ? (
        deletedDiagrams.length === 0 ? (
          <div className="text-center py-12 border border-dashed border-border rounded-lg">
            <svg className="w-12 h-12 text-muted-foreground mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            <p className="text-muted-foreground">No deleted diagrams</p>
          </div>
        ) : (
          <div className="space-y-3">
            {deletedDiagrams.map((diagram) => (
              <div
                key={diagram.id}
                className="flex items-center justify-between p-4 border border-border rounded-lg bg-muted/30"
              >
                <div>
                  <h3 className="font-medium text-muted-foreground">{diagram.name}</h3>
                  <p className="text-xs text-muted-foreground mt-1">
                    {/* #1034: list endpoint no longer ships `graph`; read columns. */}
                    {diagram.nodeCount ?? 0} nodes, {diagram.edgeCount ?? 0} edges
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleRestoreDiagram(diagram.id)}
                    disabled={restoringDiagramId === diagram.id}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary border border-primary rounded-md hover:bg-primary/10 transition-colors disabled:opacity-50"
                  >
                    {restoringDiagramId === diagram.id ? (
                      <>
                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Restoring...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Restore
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => handlePermanentDeleteDiagram(diagram.id)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 border border-red-300 dark:border-red-700 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Delete Forever
                  </button>
                </div>
              </div>
            ))}
          </div>
        )
      ) : filteredDiagrams.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-border rounded-lg">
          {diagrams.length > 0 ? (
            <>
              <p className="text-muted-foreground mb-4">
                Generate AI-powered architecture documents to analyze specific aspects of your codebase
              </p>
              <button
                onClick={() => setShowCreateDiagramModal(true)}
                disabled={isCreatingDiagrams || !canAnalyze}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Create Diagram
              </button>
            </>
          ) : (
            <p className="text-muted-foreground">
              {canAnalyze
                ? `No architecture documents yet. Analyze the ${isLocalProject ? 'local folder' : 'repository'} to generate them.`
                : 'No architecture documents yet.'}
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {groupedDiagrams.map((item, groupIndex) => {
            // Render split diagram group
            if (item.type === 'split-group') {
              const totalDiagrams = 1 + item.containers.reduce((sum, c) => sum + 1 + c.domains.length, 0);
              return (
                <div key={`split-group-${groupIndex}`} className="border border-border rounded-lg overflow-hidden">
                  {/* Group Header */}
                  <div className="bg-muted/50 px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                      </svg>
                      <h3 className="font-semibold">Component Dependencies</h3>
                      <span className="text-xs text-muted-foreground">({totalDiagrams} diagrams)</span>
                    </div>
                    <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">Split View</span>
                  </div>

                  {/* Overview Diagram */}
                  {item.overview && (
                    <div className="p-4 border-b border-border">
                      <Link
                        to={`/diagrams/${item.overview.id}`}
                        className="block p-4 border border-border rounded-lg hover:border-primary transition-colors bg-gradient-to-r from-primary/5 to-transparent"
                      >
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064" />
                          </svg>
                          <span className="font-medium">Overview</span>
                          <span className="text-xs text-muted-foreground">
                            {/* #1034 */}
                            {item.overview.nodeCount ?? 0} nodes
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">Complete dependency graph across all containers</p>
                      </Link>
                    </div>
                  )}

                  {/* Container-level Diagrams */}
                  {item.containers.length > 0 && (
                    <div className="p-4 space-y-3">
                      <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Containers</div>
                      {item.containers.map((container) => {
                        const isExpanded = expandedContainers.has(container.containerName);
                        const hasDomains = container.domains.length > 0;

                        return (
                          <div key={container.containerName} className="border border-border rounded-lg overflow-hidden">
                            {/* Container Header */}
                            <div className="flex items-center">
                              {hasDomains && (
                                <button
                                  onClick={() => {
                                    const newSet = new Set(expandedContainers);
                                    if (isExpanded) {
                                      newSet.delete(container.containerName);
                                    } else {
                                      newSet.add(container.containerName);
                                    }
                                    setExpandedContainers(newSet);
                                  }}
                                  className="p-3 hover:bg-muted/50 transition-colors"
                                >
                                  <svg
                                    className={`w-4 h-4 text-muted-foreground transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                  </svg>
                                </button>
                              )}
                              <Link
                                to={`/diagrams/${container.diagram.id}`}
                                className={`flex-1 p-3 hover:bg-muted/30 transition-colors flex items-center justify-between ${!hasDomains ? 'pl-5' : ''}`}
                              >
                                <div className="flex items-center gap-2">
                                  <svg className="w-4 h-4 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8" />
                                  </svg>
                                  <span className="font-medium">{container.containerName}</span>
                                </div>
                                <span className="text-xs text-muted-foreground">
                                  {/* #1034 */}
                                  {container.diagram.nodeCount ?? 0} nodes
                                  {hasDomains && ` / ${container.domains.length} domains`}
                                </span>
                              </Link>
                            </div>

                            {/* Domain-level Diagrams */}
                            {isExpanded && hasDomains && (
                              <div className="border-t border-border bg-muted/20 p-3 space-y-2">
                                {container.domains.map((domain) => {
                                  const domainName = domain.name.replace(`Component Dependencies: ${container.containerName}/`, '');
                                  return (
                                    <Link
                                      key={domain.id}
                                      to={`/diagrams/${domain.id}`}
                                      className="block p-2 pl-8 rounded hover:bg-muted/50 transition-colors"
                                    >
                                      <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                          <svg className="w-3 h-3 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                          </svg>
                                          <span className="text-sm">{domainName}</span>
                                        </div>
                                        <span className="text-xs text-muted-foreground">
                                          {/* #1034 */}
                                          {domain.nodeCount ?? 0} nodes
                                        </span>
                                      </div>
                                    </Link>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            // Render regular diagram
            const diagram = item.diagram;
            const description = getDiagramDescription(diagram);
            const methodology = getDiagramMethodology(diagram);
            const generatorName = formatGeneratorName(diagram.generatedBy);
            const dataSourceCount = getDataSourceCount(diagram.dataSources);
            const hasAnalysisInfo = generatorName || methodology || dataSourceCount > 0 || diagram.confidence;

            // Issue #241: Check if this diagram is being regenerated
            const isRegenerating = regeneratingDiagrams.has(diagram.id);
            const regenState = isRegenerating ? regeneratingDiagrams.get(diagram.id) : null;

            return (
              <div
                key={diagram.id}
                className={`relative group block p-6 border rounded-lg transition-all ${
                  isRegenerating
                    ? 'border-primary border-2 ring-2 ring-primary/30 bg-primary/5'
                    : 'border-border hover:border-primary'
                }`}
              >
                {/* Issue #241: Regeneration overlay - visible at card level */}
                {isRegenerating && regenState && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/80 rounded-lg">
                    <div className="flex flex-col items-center gap-2 px-4 py-3 bg-background border border-primary/30 shadow-lg rounded-lg">
                      {regenState.status === 'failed' ? (
                        <>
                          <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                          <span className="text-sm font-medium text-red-600 dark:text-red-400">
                            {regenState.error || 'Regeneration failed'}
                          </span>
                        </>
                      ) : (
                        <>
                          <svg className="w-6 h-6 animate-spin text-primary" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          {/* Issue #373: Show pipeline stage instead of generic "Regenerating..." */}
                          <span className="text-sm font-medium text-foreground">
                            {regenState.stage || 'Regenerating...'}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {(() => {
                              const elapsedSecs = Math.floor((Date.now() - regenState.startTime) / 1000);
                              return elapsedSecs < 60
                                ? `${elapsedSecs}s elapsed`
                                : `${Math.floor(elapsedSecs / 60)}m ${elapsedSecs % 60}s elapsed`;
                            })()}
                            {regenState.progress > 0 && ` • ${regenState.progress}%`}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                )}
                <Link
                  to={diagram.type === 'e2eFlow' || diagram.type === 'e2eUserFlows'
                    ? `/projects/${projectId}/flows`
                    : `/diagrams/${diagram.id}`}
                  className="block"
                >
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-lg font-semibold">{diagram.name}</h3>
                  {(diagram.type === 'e2eFlow' || diagram.type === 'e2eUserFlows') && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                      E2E Flows
                    </span>
                  )}
                </div>
                {/* Issue #184: Make description text clickable to expand/collapse */}
                {description && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setExpandedDescription(expandedDescription === diagram.id ? null : diagram.id);
                    }}
                    className={`text-sm text-muted-foreground mb-3 text-left hover:text-foreground transition-colors ${expandedDescription === diagram.id ? '' : 'line-clamp-2'}`}
                    title={expandedDescription === diagram.id ? 'Click to collapse' : 'Click to expand'}
                  >
                    {description}
                  </button>
                )}

                {/* Analysis Pipeline Info */}
                {hasAnalysisInfo && (
                  <div className="mb-3 p-2 bg-muted/50 rounded text-xs space-y-1">
                    {generatorName && (
                      <div className="flex items-center gap-1.5">
                        <span className="text-muted-foreground">Generator:</span>
                        <span className="font-medium">{generatorName}</span>
                      </div>
                    )}
                    {/* Issue #184: Make methodology text clickable to expand/collapse */}
                    {methodology && (
                      <div className="flex items-start gap-1.5">
                        <span className="text-muted-foreground shrink-0">Method:</span>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setExpandedMethodology(expandedMethodology === diagram.id ? null : diagram.id);
                          }}
                          className={`text-left hover:text-primary transition-colors ${expandedMethodology === diagram.id ? '' : 'line-clamp-1'}`}
                          title={expandedMethodology === diagram.id ? 'Click to collapse' : 'Click to expand'}
                        >
                          {methodology}
                        </button>
                      </div>
                    )}
                    {dataSourceCount > 0 && (
                      <SourceFilesDisplay
                        dataSources={diagram.dataSources as DataSource[]}
                        diagramId={diagram.id}
                        maxVisible={3}
                      />
                    )}
                    {diagram.confidence != null && (
                      <div className="flex items-center gap-1.5">
                        <span className="text-muted-foreground">Confidence:</span>
                        <span className={diagram.confidence >= 0.7 ? 'text-green-600 dark:text-green-400' : diagram.confidence >= 0.4 ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-600 dark:text-red-400'}>
                          {Math.round(diagram.confidence * 100)}%
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Timestamp and version info (Issue #158) */}
                <div className="flex items-center justify-between text-xs text-muted-foreground mt-2">
                  <span>
                    {/* #1034: list response no longer ships `graph`. */}
                    {(diagram.type === 'e2eFlow' || diagram.type === 'e2eUserFlows')
                      ? `${diagram.nodeCount ?? 0} interactions`
                      : `${diagram.nodeCount ?? 0} nodes, ${diagram.edgeCount ?? 0} edges`}
                  </span>
                  <div className="flex items-center gap-2">
                    {diagram.version && diagram.version > 1 && (
                      <span className="text-primary">v{diagram.version}</span>
                    )}
                    <span title={diagram.updatedAt ? new Date(diagram.updatedAt).toLocaleString() : ''}>
                      {formatDate(diagram.updatedAt)}
                    </span>
                  </div>
                </div>
              </Link>
              {/* Action buttons - shown on hover, hidden when regenerating (Issue #158, #239, #241) */}
              {/* Issue #241: Simplified to just show buttons when not regenerating */}
              {!isRegenerating && (
                <div className="absolute top-2 right-2 z-20 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-auto">
                  {/* Regenerate button */}
                  <button
                    onClick={(e) => handleRegenerateDiagram(e, diagram.id)}
                    className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors bg-background/80"
                    title="Regenerate diagram"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </button>
                  {/* Delete button */}
                  <button
                    onClick={(e) => handleDiagramDeleteClick(e, diagram)}
                    className="p-1.5 rounded-md hover:bg-red-100 dark:hover:bg-red-900/30 text-muted-foreground hover:text-red-600 dark:hover:text-red-400 transition-colors bg-background/80"
                    title="Delete diagram"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              )}
              </div>
            );
          })}
        </div>
      )}

      {/* Delete Confirmation Dialog (Issue #159) */}
      <DeleteConfirmDialog
        isOpen={deleteDialogOpen}
        onClose={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
        title="Delete Project?"
        itemName={project.name}
        itemType="project"
        cascadeInfo={deletePreview || undefined}
      />

      {/* Diagram Delete Confirmation Dialog (Issue #158) */}
      <DeleteConfirmDialog
        isOpen={diagramDeleteDialogOpen}
        onClose={handleDiagramDeleteClose}
        onConfirm={handleDiagramDeleteConfirm}
        title="Delete Diagram?"
        itemName={diagramToDelete?.name || ''}
        itemType="diagram"
      />

      {/* Detection Settings Modal (Issue #381) */}
      {showDetectionSettings && projectId && (
        <DetectionSettings
          projectId={projectId}
          onClose={() => setShowDetectionSettings(false)}
        />
      )}

      {/* File Exclusion Settings Modal (Issue #504) */}
      {showExclusionSettings && projectId && (
        <ExclusionSettings
          projectId={projectId}
          onClose={() => setShowExclusionSettings(false)}
        />
      )}

      {/* Create Diagram Modal */}
      <DiagramSelectionModal
        isOpen={showCreateDiagramModal}
        onClose={() => setShowCreateDiagramModal(false)}
        onStartAnalysis={handleCreateDiagrams}
        isAnalyzing={isCreatingDiagrams}
      />

      {/* Analysis Preview Dialog */}
      <AnalysisPreviewDialog
        projectId={project?.id || ''}
        isOpen={showAnalysisPreview}
        onClose={() => setShowAnalysisPreview(false)}
        onConfirm={() => {
          setShowAnalysisPreview(false);
          handleAnalyze();
        }}
        sourceType={project?.sourceType || 'local'}
        localPath={project?.localPath || undefined}
      />

      {/* veodiagram-issues#25 — pre-analysis file scope review */}
      <PreAnalysisReview
        isOpen={showFileScopeReview}
        projectId={projectId || ''}
        onCancel={() => setShowFileScopeReview(false)}
        onConfirm={runAnalyze}
      />

      {/* veodiagram-issues#41 — post-analysis route/URL review */}
      {projectId && (
        <RouteUrlReviewModal
          isOpen={showRouteUrlReview}
          projectId={projectId}
          onClose={handleRouteUrlReviewClose}
          onSaved={handleRouteUrlReviewSaved}
        />
      )}
    </div>
  );
}
